import { type EventInteractionWithContext, EventInteractions } from '../types';

/**
 * Base class for all interaction loggers
 *
 * Provides common functionality for filtering and processing interactions
 * while allowing each logger to implement its own transformation and logging logic
 */
export abstract class BaseInteractionsLogger<T> {
  protected allowedEvents: EventInteractions[] = Object.values(EventInteractions);
  protected excludedEvents: EventInteractions[] = [];

  /**
   * Transforms an interaction into the format expected by this logger
   *
   * @param interaction - The interaction to transform
   * @returns The transformed interaction in logger-specific format
   */
  protected abstract transformInteraction(interaction: EventInteractionWithContext): T;

  /**
   * Logs the transformed interactions to the destination
   *
   * @param transformedInteractions - Array of transformed interactions
   */
  protected abstract log(transformedInteractions: T[]): void;

  /**
   * Processes a batch of interactions
   *
   * This method:
   * 1. Filters interactions based on allowed/excluded events
   * 2. Transforms each interaction using the logger-specific logic
   * 3. Logs the transformed interactions
   *
   * @param interactions - Array of interactions to process
   */
  public run(interactions: EventInteractionWithContext[]) {
    const filtered = this.filterInteractions(interactions);

    if (filtered.length === 0) {
      return;
    }

    const transformed = filtered.map((interaction) => this.transformInteraction(interaction));
    this.log(transformed);
  }

  /**
   * Filter with custom logic the interactions.
   * @param interaction - The interaction to filter.
   * @returns If the interaction should be logged or not
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected filterEventsBy(_: EventInteractionWithContext): boolean {
    return true;
  }

  /**
   * Filters interactions based on allowed and excluded events
   *
   * @param interactions - Array of interactions to filter
   * @returns Filtered array of interactions
   */
  protected filterInteractions(interactions: EventInteractionWithContext[]): EventInteractionWithContext[] {
    return interactions
      .filter(({ action }) => this.allowedEvents.includes(action) && !this.excludedEvents.includes(action))
      .filter(this.filterEventsBy);
  }
}
